import { useMemo, useState } from 'react';
import { Button } from '@/components/common/Button';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useLookup } from '@/hooks/useLookups';
import { formatDateDDMMYYYY } from '../format';
import type { ClientDetail } from '../types';
import type { UpdateKycPayload } from '../useClientDetail';

// CBS-internal KYC status IDs for the UpdateKyc API payload.
// These are NOT webapp reference table IDs — the CBS backend maps them
// to labels before talking to the webapp.
const CBS_KYC_ACTION = { APPROVED: 2, SUSPENDED: 4 } as const;

interface KYCTabProps {
  client: ClientDetail;
  onUpdateKyc: (payload: UpdateKycPayload) => Promise<void>;
  onActivate: (active: boolean) => Promise<void>;
  onRefresh: () => Promise<void>;
}

/** Une liste de codes se lit `US, GB, DE`, et une liste vide comme un vide. */
function joinCodes(codes: string[] | undefined | null): string | null {
  if (!codes || codes.length === 0) return null;
  return codes.join(', ');
}

function Field({ label, value }: { label: string; value: string | undefined | null }) {
  const display = value && value !== '—' ? value : '—';
  const filled = display !== '—';
  return (
    <div className="mb-2">
      <div className="mb-0.5 text-[10px] uppercase tracking-[0.5px] text-[#999]">{label}</div>
      <div
        className={`whitespace-pre-wrap break-words text-[13px] font-medium ${
          filled ? 'text-[#1a1a1a]' : 'text-[#ccc]'
        }`}
      >
        {display}
      </div>
    </div>
  );
}

/**
 * Le nom du pays vient du referentiel charge, plus d'une liste figee (#222).
 * Tant qu'il n'est pas la, on montre le code : il dit deja quelque chose, et
 * c'est de toute facon ce qui est stocke.
 */
function renderCountry(
  code: string | null | undefined,
  nameByCode: Map<string, string>,
): string | null {
  if (!code) return null;
  const normalised = code.trim().toUpperCase();
  if (!normalised) return null;
  const name = nameByCode.get(normalised);
  return name ? `${name} (${normalised})` : normalised;
}

function fmtCurrency(value: number | null | undefined, currency = 'EUR'): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  } catch {
    // Intl.NumberFormat throws on an unknown / malformed currency code.
    // Returning null surfaces the bad code as an em-dash through Field
    // rather than printing a misleading symbol-less "1,234.56 EUR" — a
    // KYC reviewer should see "we cannot format this" not a fake number.
    return null;
  }
}

function fmtCount(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return value.toLocaleString('en-US');
}

/**
 * Render a Yes/No/Unknown triplet for compliance flags.
 *
 * `has_pep` and `has_us_person` are typed as optional booleans on the
 * detail schema — the API guarantees a boolean for corporate clients, but
 * a missing business sub-object (e.g. an individual client, or a
 * non-detail response) leaves the value undefined. Collapsing undefined
 * to "No" hides the difference between "definitely not a PEP" and "we
 * don't know yet" — a misleading distinction for a KYC reviewer.
 */
function formatKycBoolean(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Unknown';
}

function lookupId(value: number | null | undefined): string | null {
  // Phase 2: surface the raw numeric ID as a placeholder.
  // Phase 3b (B11) will resolve the ID to a human label via the
  // legal-forms / business-sectors / business-activities lookup
  // dropdowns. Keeping the raw ID stops the field from appearing
  // empty for KYC reviewers in the meantime.
  if (value == null) return null;
  return `#${value}`;
}

export function KYCTab({ client, onUpdateKyc, onActivate, onRefresh }: KYCTabProps) {
  const { items: countries } = useLookup('countries');
  const countryNameByCode = useMemo(
    () => new Map(countries.map((c) => [c.code ?? '', c.label])),
    [countries],
  );

  const [confirmAction, setConfirmAction] = useState<
    'approve' | 'suspend' | 'activate' | 'deactivate' | null
  >(null);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      if (confirmAction === 'approve') {
        await onUpdateKyc({ kycStatusId: CBS_KYC_ACTION.APPROVED });
      } else if (confirmAction === 'suspend') {
        await onUpdateKyc({ kycStatusId: CBS_KYC_ACTION.SUSPENDED });
      } else if (confirmAction === 'activate') {
        await onActivate(true);
      } else if (confirmAction === 'deactivate') {
        await onActivate(false);
      }
      await onRefresh();
    } catch {
      // Error already toasted by hook — skip onRefresh on failure
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  const statusLower = client.kyc_status_name.toLowerCase();
  const isPending = statusLower === 'pending';
  const isApproved = statusLower === 'approved';
  const isRejected = statusLower === 'rejected';
  const isIndividual = client.client_type_name === 'Individual';

  return (
    <div>
      {/* Action buttons */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex gap-2">
          {client.is_active ? (
            <Button variant="outline" size="sm" onClick={() => setConfirmAction('deactivate')}>
              Deactivate Client
            </Button>
          ) : (
            <Button size="sm" onClick={() => setConfirmAction('activate')}>
              Activate Client
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {(isPending || isRejected) && (
            <Button
              size="sm"
              className="bg-[#2e7d32] hover:bg-[#1b5e20]"
              onClick={() => setConfirmAction('approve')}
            >
              Approve KYC
            </Button>
          )}
          {isApproved && (
            <Button variant="destructive" size="sm" onClick={() => setConfirmAction('suspend')}>
              Suspend Client
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {/* Card 1 — Company / Identity */}
        <div className="rounded-xl border border-[#eee] bg-white p-5">
          <div className="mb-4 text-[14px] font-bold text-[#1a1a1a]">
            {isIndividual ? 'Client Details' : 'Company Details'}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={isIndividual ? 'Full Name' : 'Company Name'}
              value={client.display_name || client.company_name}
            />
            <Field
              label={isIndividual ? 'Country' : 'Country of Incorporation'}
              value={renderCountry(client.country_code, countryNameByCode)}
            />
            <Field label="Legal Form" value={lookupId(client.legal_form_id ?? null)} />
            <Field
              label="Incorporation Date"
              value={formatDateDDMMYYYY(client.incorporation_date)}
            />
            <Field label="Incorporation Number" value={client.incorporation_number} />
            <Field label="TIN" value={client.tin} />
            <Field label="Website" value={client.website} />
            <Field label="Business Sector" value={lookupId(client.business_sector_id ?? null)} />
            <Field
              label="Business Activity"
              value={lookupId(client.business_activity_id ?? null)}
            />
          </div>
        </div>

        {/* Card 2 — Contact + Status */}
        <div className="rounded-xl border border-[#eee] bg-white p-5">
          <div className="mb-4 text-[14px] font-bold text-[#1a1a1a]">Contact & Status</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={client.contact_email} />
            <Field label="Telephone" value={client.telephone} />
            <Field label="KYC Status" value={client.kyc_status_name} />
            <Field label="Risk Level" value={client.risk_level_name} />
            <Field label="Active" value={client.is_active ? 'Yes' : 'No'} />
            <Field label="Created" value={formatDateDDMMYYYY(client.created_at)} />
          </div>
        </div>

        {/* Card 3 — Registered + Business addresses */}
        <div className="col-span-2 rounded-xl border border-[#eee] bg-white p-5">
          <div className="mb-4 text-[14px] font-bold text-[#1a1a1a]">Addresses</div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="mb-2 text-[11px] uppercase tracking-[0.5px] text-[#888]">
                Registered
              </div>
              <Field label="Line 1" value={client.registered_address_line1} />
              <Field label="Line 2" value={client.registered_address_line2} />
              <Field label="City" value={client.registered_address_city} />
              <Field label="State / Region" value={client.registered_address_state} />
              <Field label="ZIP / Postal" value={client.registered_address_zip} />
              <Field
                label="Country"
                value={renderCountry(client.registered_address_country_code, countryNameByCode)}
              />
            </div>
            <div>
              <div className="mb-2 text-[11px] uppercase tracking-[0.5px] text-[#888]">
                Business
              </div>
              <Field label="Line 1" value={client.business_address_line1} />
              <Field label="Line 2" value={client.business_address_line2} />
              <Field label="City" value={client.business_address_city} />
              <Field label="State / Region" value={client.business_address_state} />
              <Field label="ZIP / Postal" value={client.business_address_zip} />
              <Field
                label="Country"
                value={renderCountry(client.business_address_country_code, countryNameByCode)}
              />
            </div>
          </div>
        </div>

        {/* Card 4 — Account usage / compliance */}
        <div className="col-span-2 rounded-xl border border-[#eee] bg-white p-5">
          <div className="mb-4 text-[14px] font-bold text-[#1a1a1a]">
            Account Usage & Compliance
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Monthly Income" value={fmtCurrency(client.monthly_income)} />
            <Field label="Source of Income" value={client.source_of_income} />
            <Field label="Activity Description" value={client.activity_description} />
            <Field label="Purpose of Account" value={client.purpose_of_account} />
            <Field label="Intended Use" value={client.intended_use} />
            <Field label="Planned Countries In" value={joinCodes(client.planned_countries_in)} />
            <Field label="Planned Countries Out" value={joinCodes(client.planned_countries_out)} />
            <Field label="Planned Currencies" value={joinCodes(client.planned_currencies)} />
            <Field
              label="Est. Monthly Incoming Volume"
              value={fmtCurrency(client.estimated_incoming_volume)}
            />
            <Field
              label="Est. Monthly Incoming Tx"
              value={fmtCount(client.estimated_incoming_tx_count)}
            />
            <Field
              label="Est. Monthly Outgoing Volume"
              value={fmtCurrency(client.estimated_outgoing_volume)}
            />
            <Field
              label="Est. Monthly Outgoing Tx"
              value={fmtCount(client.estimated_outgoing_tx_count)}
            />
            <Field label="Has PEP" value={formatKycBoolean(client.has_pep)} />
            <Field label="Has US Person" value={formatKycBoolean(client.has_us_person)} />
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmAction !== null}
        title={
          confirmAction === 'approve'
            ? 'Approve KYC'
            : confirmAction === 'suspend'
              ? 'Suspend Client'
              : confirmAction === 'activate'
                ? 'Activate Client'
                : 'Deactivate Client'
        }
        message={
          confirmAction === 'approve'
            ? 'Are you sure you want to approve KYC for this client?'
            : confirmAction === 'suspend'
              ? 'Suspend this client? They will lose access to the platform.'
              : confirmAction === 'activate'
                ? 'Are you sure you want to activate this client?'
                : 'Are you sure you want to deactivate this client?'
        }
        confirmText={submitting ? 'Processing...' : 'Confirm'}
        onConfirm={() => void handleConfirm()}
        onCancel={() => setConfirmAction(null)}
        isDangerous={confirmAction === 'suspend' || confirmAction === 'deactivate'}
      />
    </div>
  );
}
